// src/context/UserContext.jsx
import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { AuthService } from "../services/AuthService";
import { CardService } from "../services/CardService";
import { UserService } from "../services/UserService";
import { getUserPersonas } from "../services/AnamnesisService";
import { getAuthToken, setAuthToken } from "../utils/authToken";
import { clearPersonaSessionState, switchActivePersona } from "../utils/personaStorage";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  // Agora o usrId é sempre derivado do backend (sessão), não do localStorage
  const [usrId, setUsrId] = useState(null);
  const [sessionAuthenticated, setSessionAuthenticated] = useState(false);

  // Dados completos retornados pela API (user, addresses, payment_methods etc.)
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Estados já existentes
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [userName, setUserName] = useState("Visitante");
  const [sex, setSex] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");

  // Anamnese permanece
  const [userAnamnesis, setUserAnamnesis] = useState({
    name: "",
    gender: "",
    birth: "",
    weight: "",
    ethnicity: "",
    dosage: 100,
    searchFor: [],
    user_chat: [],
  });

  // --- 1) Detecta sessão no servidor (me.php) ao iniciar a app
  const getUserId = useCallback((userObj) => {
    return userObj?.usr_id ?? userObj?.id ?? userObj?.user_id ?? null;
  }, []);

  const refreshMe = useCallback(async () => {
    setLoadingAuth(true);
    try {
      if (!getAuthToken()) {
        setUser(null);
        setUsrId(null);
        setSessionAuthenticated(false);
        return;
      }
      const res = await AuthService.me(); // { authenticated, user }
      if (res?.authenticated) {
        const userObj = res?.user || null;
        const resolvedId = getUserId(userObj);
        setUser(userObj);
        setUsrId(resolvedId);
        setSessionAuthenticated(true);
      } else {
        setUser(null);
        setUsrId(null);
        setSessionAuthenticated(false);
      }
    } catch {
      setUser(null);
      setUsrId(null);
      setSessionAuthenticated(false);
    } finally {
      setLoadingAuth(false);
    }
  }, [getUserId]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Helper para mapear resposta da API (clean) para formato legado do front (add_)
  const mapAddressFromApi = (addr) => {
    if (!addr) return null;
    const normalizedCountry = String(
      addr.add_country || addr.country || "BR",
    ).toUpperCase();

    // Se já tiver add_id, assume que está no formato antigo ou misto
    if (addr.add_id) {
      return {
        ...addr,
        add_country: normalizedCountry,
      };
    }

    return {
      ...addr,
      // Mapeia chaves clean (API) -> chaves legacy (Front)
      add_id: addr.id,
      add_zip: addr.zip,
      add_street: addr.street,
      add_number: addr.number,
      add_complement: addr.complement,
      add_neighborhood: addr.neighborhood,
      add_city: addr.city,
      add_state: addr.state,
      add_type: addr.type,
      add_country: normalizedCountry,
    };
  };

  // ---- ENDEREÇOS ----
  const reloadAddresses = useCallback(async () => {
    if (!usrId) return;
    setLoadingAddresses(true);
    try {
      const res = await UserService.getAddresses(usrId);
      if (res?.status === "success") {
        const rawList = res.data || [];
        const mappedList = Array.isArray(rawList)
          ? rawList.map(mapAddressFromApi)
          : [];
        setAddresses(mappedList);
      }
    } catch (e) {
      console.error("[UserContext] reloadAddresses erro", e);
    } finally {
      setLoadingAddresses(false);
    }
  }, [usrId]);

  const addOrUpdateAddress = useCallback(
    async (payload) => {
      if (!usrId) throw new Error("Usuário não autenticado");

      let response = null;
      // Verifica se possui ID válido que indica Edição
      if (payload.add_id) {
        response = await UserService.updateAddress(payload.add_id, payload);
      } else {
        response = await UserService.createAddress(usrId, payload);
      }

      await reloadAddresses();
      return response?.data?.address || response?.data || response;
    },
    [usrId, reloadAddresses],
  );

  const deleteAddress = useCallback(
    async (add_id) => {
      if (!usrId) throw new Error("Usuário não autenticado");
      await UserService.deleteAddress(add_id);
      await reloadAddresses();
    },
    [usrId, reloadAddresses],
  );

  const setDefaultAddress = useCallback(
    async (add_id) => {
      if (!usrId) throw new Error("Usuário não autenticado");
      await UserService.setDefaultAddress(add_id);
      setAddresses((prev) =>
        (prev || []).map((a) => ({
          ...a,
          is_default:
            String(a.add_id || a.id) === String(add_id) ? 1 : 0,
        }))
      );
    },
    [usrId],
  );

  // ---- CARTÕES / MÉTODOS DE PAGAMENTO ----
  const reloadPaymentMethods = useCallback(async () => {
    if (!usrId) return;
    setLoadingPaymentMethods(true);
    try {
      const res = await CardService.list();
      let list = [];
      if (Array.isArray(res)) list = res;
      else if (Array.isArray(res?.data)) list = res.data;
      else if (Array.isArray(res?.result)) list = res.result;
      else if (Array.isArray(res?.cards)) list = res.cards;
      setPaymentMethods(list || []);
    } catch (e) {
      console.error("[UserContext] reloadPaymentMethods erro", e);
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, [usrId]);

  // Metodos de pagamento - mutações centralizadas
  const addPaymentMethod = useCallback(
    async (payload) => {
      if (!usrId) throw new Error("Usuário não autenticado");
        console.log('zz:payload:', JSON.stringify(payload));
      await CardService.add(payload);
      await reloadPaymentMethods();
    },
    [usrId, reloadPaymentMethods],
  );

  const updatePaymentMethod = useCallback(
    async (pm_id, data) => {
      if (!usrId) throw new Error("Usuário não autenticado");
      await CardService.update(pm_id, data);
      await reloadPaymentMethods();
    },
    [usrId, reloadPaymentMethods],
  );

  const deletePaymentMethod = useCallback(
    async (pm) => {
      if (!usrId) throw new Error("Usuário não autenticado");
      await CardService.remove(pm);
      await reloadPaymentMethods();
    },
    [usrId, reloadPaymentMethods],
  );

  const setDefaultPaymentMethod = useCallback(
    async (pm_id) => {
      if (!usrId) throw new Error("Usuário não autenticado");
      await CardService.select(pm_id);
      setPaymentMethods((prev) =>
        (prev || []).map((card) => {
          const cardId = card.pm_id ?? card.payment_method_id ?? card.id;
          const isMatch = String(cardId) === String(pm_id);
          return {
            ...card,
            is_default: isMatch ? 1 : 0,
            default: isMatch,
          };
        })
      );
    },
    [usrId],
  );

  const setAddressesFromApi = useCallback((rawList) => {
    const list = Array.isArray(rawList) ? rawList : [];
    const mapped = list.map(mapAddressFromApi).filter(Boolean);
    setAddresses(mapped);
    return mapped;
  }, []);

  const setPaymentMethodsFromApi = useCallback((rawList) => {
    const list = Array.isArray(rawList) ? rawList : [];
    setPaymentMethods(list);
    return list;
  }, []);

  // --- 2) Carrega dados completos do usuário quando tiver usrId
  const fetchUser = useCallback(async () => {
    if (!usrId) {
      setUser((u) => (u ? u : null));
      setAddresses([]);
      setPaymentMethods([]);
      return;
    }
    setLoadingUser(true);
    try {
      // Mantive sua assinatura atual: getUserData(usrId)
      const res = await UserService.getUserData();
      if (res?.status === "success" && res.data) {
        const payload = res.data; // { user, addresses, payment_methods }
        const userObj = payload.user || (res.data.id ? res.data : {}); // fallback se data for o user direto
        setUser((prev) => ({ ...prev, ...userObj }));

        // Aplica addresses e payment_methods apenas se vierem inclusos na resposta
        // Não dispara requests secundárias automaticamente aqui; apenas quando solicitado pelo componente
        if (payload.addresses) {
          const raw = Array.isArray(payload.addresses) ? payload.addresses : [];
          setAddresses(raw.map(mapAddressFromApi));
        }

        if (payload.payment_methods) {
          setPaymentMethods(
            Array.isArray(payload.payment_methods)
              ? payload.payment_methods
              : [],
          );
        }
      } else {
        setAddresses([]);
        setPaymentMethods([]);
      }
    } catch (e) {
      console.error("[UserContext] fetchUser erro", e);
    } finally {
      setLoadingUser(false);
    }
  }, [usrId]);

  useEffect(() => {
    if (usrId) fetchUser();
  }, [usrId, fetchUser]);

  // Deriva userName (primeiro nome) e welcomeMessage
  useEffect(() => {
    let baseName =
      user?.usr_name || user?.name || userAnamnesis?.name || "Visitante";

    if (baseName) {
      baseName = String(baseName).trim().split(" ")[0];
    } else {
      baseName = "Visitante";
    }

    setUserName(baseName);
    setWelcomeMessage(
      `${userAnamnesis?.gender === "2" ? "Bem-vinda" : "Bem-vindo"}, ${baseName}!`,
    );
  }, [user, userAnamnesis.name, userAnamnesis.gender]);

  // ---- ANAMNESE ----
  const updateAnamnesis = (field, value) => {
    setUserAnamnesis((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ---- LOGIN / LOGOUT (agora via sessão, sem localStorage) ----
  const login = useCallback(
    async (email, password) => {
      const res = await AuthService.login(email, password);
      if (!res?.ok) {
        throw new Error(res?.message || "Falha no login");
      }
      // Limpa dados de anamnese de sessão anterior e persona antes de carregar o novo usuário
      clearPersonaSessionState();
      localStorage.removeItem("selectedProfileId");
      setSessionAuthenticated(true);
      // Se o backend devolveu user, aplica direto; caso não, chama refreshMe()
      const resolvedId = getUserId(res?.user);
      if (res?.user) {
        setUser(res.user);
        setUsrId(resolvedId);
      } else {
        await refreshMe();
      }

      // NOVO: Garantir que a persona principal seja carregada e salva no localStorage no momento exato do login
      try {
        const personas = await getUserPersonas();
        const owner = (personas || []).find((p) => Number(p?.is_owner || 0) === 1) || personas?.[0] || null;
        if (owner?.per_id || owner?.persona_id || owner?.id) {
          const ownerId = owner.per_id || owner.persona_id || owner.id;
          switchActivePersona(ownerId, resolvedId);
        }
      } catch (e) {
        console.error("Erro ao buscar owner persona no login:", e);
      }

      return res;
    },
    [refreshMe, getUserId],
  );

  const loginWithToken = useCallback(
    async (token, userObj) => {
      if (!token) throw new Error("Token inválido");
      clearPersonaSessionState();
      setAuthToken(token);
      setSessionAuthenticated(true);

      let resolvedId = getUserId(userObj);
      if (userObj) {
        setUser(userObj);
        setUsrId(resolvedId);
      } else {
        await refreshMe();
      }

      // NOVO: Garantir que a persona principal seja carregada e salva no localStorage no momento exato do loginWithToken
      try {
        const personas = await getUserPersonas();
        const owner = (personas || []).find((p) => Number(p?.is_owner || 0) === 1) || personas?.[0] || null;
        if (owner?.per_id || owner?.persona_id || owner?.id) {
          const ownerId = owner.per_id || owner.persona_id || owner.id;
          switchActivePersona(ownerId, resolvedId);
        }
      } catch (e) {
        console.error("Erro ao buscar owner persona no loginWithToken:", e);
      }
    },
    [refreshMe, getUserId],
  );

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch {}
    // Limpa estados do front
    setUsrId(null);
    setUser(null);
    setSessionAuthenticated(false);
    setAddresses([]);
    setPaymentMethods([]);
    setUserName("Visitante");
    setWelcomeMessage("Bem-vindo, Visitante!");
    setUserAnamnesis({
      name: "",
      gender: "",
      birth: "",
      weight: "",
      ethnicity: "",
      dosage: 100,
      searchFor: [],
      user_chat: [],
    });
    // Limpa completamente o localStorage e o sessionStorage a pedido do usuário
    localStorage.clear();
    sessionStorage.clear();
  }, []);

  // Valores derivados úteis
  const isAuthenticated = useMemo(
    () => !!sessionAuthenticated && !loadingAuth,
    [sessionAuthenticated, loadingAuth],
  );

  return (
    <UserContext.Provider
      value={{
        // Identificação
        usrId,
        isAuthenticated,

        // Dados completos
        user,
        setUser,

        // Endereços
        addresses,
        setAddresses,
        setAddressesFromApi,
        reloadAddresses,
        addOrUpdateAddress,
        deleteAddress,
        setDefaultAddress,

        // Pagamentos
        paymentMethods,
        setPaymentMethods,
        setPaymentMethodsFromApi,
        reloadPaymentMethods,
        addPaymentMethod,
        updatePaymentMethod,
        deletePaymentMethod,
        setDefaultPaymentMethod,

        // Nome / saudação
        userName,
        setUserName,
        welcomeMessage,
        sex,
        setSex,

        // Anamnese
        userAnamnesis,
        updateAnamnesis,

        // Loading flags
        loadingAuth,
        loadingUser,
        loadingAddresses,
        loadingPaymentMethods,

        // Sessão (novo)
        login,
        loginWithToken,
        logout,
        refreshMe,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
